import { createSelector } from 'reselect';

import { subUnitTypeSel } from '@modules/advancement/subUnits/duck/selectors';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import type * as SubUnitAdmin from '@modules/roster-v2/models/subUnitAdmin';
import { userDataInitializedSel } from '@modules/user/duck/selectors';
import {
  newPermissionsSel,
  selectedOrganizationSel,
} from '@shared/duck/selectors/general.selectors';
import { newPermissions } from '@shared/newPermissions';

/** Den/patrol features: flag on and the selected unit has dens or patrols. */
export const subUnitsEnabledSel = createSelector(
  subUnitTypeSel,
  subUnitType =>
    Boolean(featureFlags.getFlag('SBL_6977_ROSTER_V2_SUB_UNITS')) &&
    !!subUnitType,
);

export const subUnitPermissionsSel = createSelector(
  newPermissionsSel,
  (permissions): SubUnitAdmin.SubUnitRights => ({
    canEditAll: Boolean(permissions[newPermissions.EDIT_UNIT]),
    canEditOwn: Boolean(permissions[newPermissions.EDIT_SUB_UNIT]),
  }),
);

/** The selected unit's id, sent as `packId` to `denChief/add`. */
export const packIdSel = createSelector(
  selectedOrganizationSel,
  (organization): number | undefined => {
    const packId = Number(organization?.unitId);
    return Number.isFinite(packId) && packId > 0 ? packId : undefined;
  },
);

/**
 * Returns `true` when the current user's effective permissions for the selected
 * organization allow access to the Roster V2 page.
 *
 * Intentionally minimal for now (just `VIEW_ROSTER_PAGE`). This is the single
 * source of truth consumed by both the route gating thunk and the sidebar nav,
 * and is meant to grow finer-grained checks as the feature is built out.
 */
export const hasRosterV2PermissionSel = createSelector(
  newPermissionsSel,
  permissions => Boolean(permissions[newPermissions.VIEW_ROSTER_PAGE]),
);

/**
 * Aggregates everything the bootstrap gate needs in a single subscription:
 * - `ready`: user data has finished initializing (selectors below are settled).
 * - `allowed`: the user is permitted to see Roster V2.
 *
 * The feature-flag check is read in the component (flags are not stored in
 * Redux). Until `ready` is true the gate must not redirect, otherwise cold
 * deep-links would flash a false 403 before bootstrap completes.
 */
export const rosterV2GateSel = createSelector(
  userDataInitializedSel,
  hasRosterV2PermissionSel,
  (ready, allowed) => ({ ready, allowed }),
);
